import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, Phone, CheckCircle2, Clock, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';
import { contactAPI } from '../services/api';

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await contactAPI.submitForm(formData, 'cta');
      toast.success(response.message);
      setFormData({ name: '', email: '', phone: '' });
    } catch (error) {
      toast.error('Erro ao enviar solicitação. Tente novamente.');
      console.error('Erro:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: CheckCircle2, text: 'Análise de Vulnerabilidade 100% Gratuita' },
    { icon: Shield, text: 'Sem Compromisso ou Obrigação' },
    { icon: Clock, text: 'Resposta em até 24 horas' },
    { icon: Users, text: 'Consultores Especializados em Telecomunicações' }
  ];

  return (
    <section
      id="contato"
      className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-[#0079be] relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://images.unsplash.com/photo-1683322499436-f4383dd59f5a"
          alt="Network Background"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Não Deixe Sua Operação Vulnerável
          </h2>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Cada minuto de downtime custa dinheiro. A TMC oferece a solução definitiva de redundância com diversidade de meio. Comece sua análise gratuita hoje.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Contact Options */}
          <div className="space-y-8 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            {/* WhatsApp Button */}
            <a
              href="https://wa.me/5511986380017?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20as%20soluções%20de%20redundância%20da%20TMC."
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button
                size="lg"
                className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-6 text-lg transition-all duration-300 hover:shadow-2xl group"
              >
                <MessageCircle className="w-6 h-6 mr-2 group-hover:scale-110 transition-transform duration-300" />
                Fale com Consultor via WhatsApp
              </Button>
            </a>

            {/* Phone Button */}
            <a href="tel:+551135352954" className="block">
              <Button
                size="lg"
                variant="outline"
                className="w-full bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0079be] font-semibold py-6 text-lg transition-all duration-300 hover:shadow-2xl group"
              >
                <Phone className="w-6 h-6 mr-2 group-hover:scale-110 transition-transform duration-300" />
                Ligar: (11) 3535-2954
              </Button>
            </a>

            {/* Benefits List */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center space-x-3 text-white bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20"
                  >
                    <Icon className="w-6 h-6 text-green-400 flex-shrink-0" />
                    <span className="font-medium">{benefit.text}</span>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-yellow-400 font-semibold text-lg">
              Não espere pela próxima falha. Proteja sua operação agora mesmo.
            </p>
          </div>

          {/* Right - Contact Form */}
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Solicite uma Análise Gratuita
              </h3>
              <p className="text-gray-600 mb-6">
                Preencha o formulário e nossa equipe entrará em contato
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Nome completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="E-mail corporativo"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    placeholder="Telefone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0079be] hover:bg-[#005a8c] text-white font-semibold py-6 text-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Enviando...' : 'Solicitar Análise Gratuita'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;